// AI Usage & Responsible AI Policy for CareerOS-AI.
import { Link } from "react-router-dom";
import LegalLayout, { bullets } from "./LegalLayout";

const EFFECTIVE = "July 16, 2026";

const SECTIONS = [
  { n: 1, id: "where-we-use-ai", title: "Where we use AI",
    body: (<>
      <p>CareerOS-AI uses large language models and other AI systems in the following features:</p>
      {bullets([
        "Resume parsing, scoring, and rewriting.",
        "Cover-letter generation.",
        "Chat with your AI career coach.",
        "Career roadmap and insight generation.",
        "Salary benchmarking commentary (numbers come from our data; interpretation from AI).",
        "Interview question generation and answer evaluation.",
        "Salary-negotiation script writing.",
        "Financial-plan recommendations and commentary.",
        "Skills and personality assessment interpretation.",
        "Support-agent responses in the in-app help widget.",
      ])}
    </>) },
  { n: 2, id: "models-and-providers", title: "Models and providers",
    body: (<>
      <p>We currently rely on external model providers, including Anthropic (Claude), OpenAI (GPT), and Google (Gemini). We select models per feature based on quality, latency, cost, and regional availability.</p>
      <p>Our provider agreements require:</p>
      {bullets([
        "No use of your prompts or CareerOS content to train the provider's public models.",
        "Short retention windows (typically 30 days or less) for abuse-monitoring only.",
        "Regional data-residency controls where offered.",
      ])}
    </>) },
  { n: 3, id: "how-we-build-prompts", title: "How we build prompts",
    body: (<>
      <p>Every user-facing AI prompt automatically receives a localized context block built from your profile — country, region, city, currency, timezone, tax authority, retirement vehicles, employment framework, spelling, and date format. This lets the AI give locally-accurate advice instead of US defaults.</p>
      <p>We do not include other users&apos; data, private business rules, or your billing details in prompts.</p>
    </>) },
  { n: 4, id: "accuracy-and-limits", title: "Accuracy and limits",
    body: (<>
      <p>AI systems make mistakes. We design CareerOS-AI to reduce them but we do not eliminate them. Concretely:</p>
      {bullets([
        "Numbers that change year-to-year (tax rates, contribution limits, minimum wage, retirement age, filing deadlines) are treated as estimates and should be verified with the appropriate authority.",
        "AI may generate confident-sounding text that is wrong. Always sanity-check important claims.",
        "AI is not a substitute for a licensed CPA, tax preparer, financial advisor, lawyer, immigration consultant, or licensed mental-health professional.",
        "We provide educational content, not regulated advice.",
      ])}
    </>) },
  { n: 5, id: "user-controls", title: "Your controls",
    body: (<>
      {bullets([
        "You choose what to save. AI outputs are shown to you before we persist them.",
        "You can regenerate any AI output.",
        "You can delete AI-generated content from your account at any time.",
        "You can export your data from Settings.",
      ])}
    </>) },
  { n: 6, id: "prohibited-uses", title: "Prohibited AI use",
    body: (<>
      <p>You agree not to use CareerOS-AI to:</p>
      {bullets([
        "Fabricate credentials, employment history, or achievements you do not hold.",
        "Impersonate another person in resumes, portfolios, or communications.",
        "Generate content that discriminates, harasses, or targets protected classes.",
        "Circumvent hiring, licensing, or educational integrity requirements.",
        "Attempt to reverse-engineer or systematically extract our prompts, tools, or datasets.",
        "Train competing AI systems on CareerOS content or outputs.",
      ])}
      <p>Violations are subject to account suspension per our <Link to="/acceptable-use">Acceptable Use Policy</Link>.</p>
    </>) },
  { n: 7, id: "human-oversight", title: "Human oversight",
    body: (<>
      <p>CareerOS-AI does not make consequential automated decisions about you (for example, we do not deny access, terminate accounts, or refuse refunds solely because an AI said so). Consequential decisions are reviewed by a human on our team.</p>
    </>) },
  { n: 8, id: "bias-and-fairness", title: "Bias and fairness",
    body: (<>
      <p>LLMs inherit biases from their training data. We take steps to reduce user-visible harm:</p>
      {bullets([
        "Localization to avoid US-centric defaults.",
        "Prompt-level rules against demographic stereotyping in salary, career, or hiring recommendations.",
        "Periodic red-team review of outputs.",
      ])}
      <p>If you spot an unfair or biased output, report it via the in-app support widget — we take these reports seriously.</p>
    </>) },
  { n: 9, id: "training-and-improvement", title: "Do we train on your data?",
    body: (<>
      <p>We do not use your account data, prompts, resumes, chats, or financial inputs to train third-party foundation models.</p>
      <p>We may use aggregated, de-identified metrics (feature usage, latency, error rates) to improve the product. Where we manually review a specific case, it is only for support, safety, or debugging — and only by authorized staff under confidentiality.</p>
    </>) },
  { n: 10, id: "regulated-domains", title: "Regulated domains",
    body: (<>
      <p>Some CareerOS-AI features touch regulated areas:</p>
      {bullets([
        "Financial planning — educational only; not investment or tax advice.",
        "Immigration — informational only; consult a licensed immigration lawyer or RCIC.",
        "Employment law — informational only; consult a licensed employment lawyer.",
        "Mental-health topics — CareerOS is not a therapy service. If you are in crisis, contact your local emergency number or a crisis line.",
      ])}
    </>) },
  { n: 11, id: "reporting", title: "Reporting AI harms",
    body: (<>
      <p>Please report anything that looks like an AI-generated harm — hallucinated facts about a person, biased outputs, unsafe advice, or a jailbreak — to <span className="font-mono">support@careeros-ai.com</span>. Include the feature, timestamp, and (if possible) a copy of the output. We treat these reports as high priority.</p>
    </>) },
  { n: 12, id: "changes", title: "Changes to this policy",
    body: <p>We will update this policy as models, providers, and best practices evolve. Material changes are communicated on the website. The &quot;Last Updated&quot; date reflects the current version.</p> },
];

const INTRO = (
  <>
    <p>CareerOS-AI is an AI-first product. This policy explains what our AI does, where it comes from, what it does not do, and how you can use it safely.</p>
    <p>Read together with our <Link to="/terms">Terms and Conditions</Link>, <Link to="/privacy">Privacy Policy</Link>, and <Link to="/acceptable-use">Acceptable Use Policy</Link>.</p>
  </>
);

export default function AIUsagePolicy() {
  return (
    <LegalLayout
      eyebrow="Legal · Responsible AI"
      title="AI Usage & Responsible AI Policy"
      subtitle="for CareerOS-AI"
      effectiveDate={EFFECTIVE}
      lastUpdated={EFFECTIVE}
      intro={INTRO}
      sections={SECTIONS}
      currentPath="/ai-policy"
    />
  );
}
